import { FileCode, FileText, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { WorkspaceFile } from "@/services/workspace";
import { cn } from "@/lib/utils";

interface FileExplorerProps {
  files: WorkspaceFile[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
  onCreate: (path: string) => void;
  onDelete: (path: string) => void;
}

function getFileIcon(path: string) {
  const ext = path.split(".").pop()?.toLowerCase();
  if (["ts", "tsx", "js", "jsx", "py", "json", "html", "css", "sql"].includes(ext || "")) {
    return <FileCode className="h-4 w-4 text-primary" />;
  }
  return <FileText className="h-4 w-4 text-muted-foreground" />;
}

export function FileExplorer({ files, selectedPath, onSelect, onCreate, onDelete }: FileExplorerProps) {
  const [newPath, setNewPath] = React.useState("");
  const [showInput, setShowInput] = React.useState(false);

  const handleCreate = () => {
    if (!newPath.trim()) return;
    onCreate(newPath.trim());
    setNewPath("");
    setShowInput(false);
  };

  return (
    <div className="flex h-full flex-col border-r bg-card">
      <div className="flex items-center justify-between border-b p-3">
        <h3 className="text-sm font-semibold">文件</h3>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowInput(true)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-2">
        {showInput && (
          <div className="mb-2 flex items-center gap-1">
            <Input
              value={newPath}
              onChange={(e) => setNewPath(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
                if (e.key === "Escape") {
                  setShowInput(false);
                  setNewPath("");
                }
              }}
              placeholder="文件路径"
              className="h-7 text-xs"
              autoFocus
            />
          </div>
        )}
        <div className="space-y-0.5">
          {files.length === 0 && <p className="px-2 py-4 text-center text-xs text-muted-foreground">暂无文件</p>}
          {files.map((file) => (
            <div
              key={file.path}
              className={cn(
                "group flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-accent",
                selectedPath === file.path && "bg-accent text-accent-foreground",
              )}
              onClick={() => onSelect(file.path)}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                {getFileIcon(file.path)}
                <span className="truncate text-xs">{file.path}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(file.path);
                }}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
